/**
 * StripeSetupInfo.js
 *
 * Reusable component that displays Stripe Connect onboarding information.
 * Used in both ClaimGift and PoolPayout flows to provide consistent messaging.
 */

import React from 'react';

function StripeSetupInfo({ showStepNumber = false, showDetailedInfo = false }) {
  if (showDetailedInfo) {
    // Detailed version for claim/payout flows
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-indigo-900 mb-2">
          💳 {showStepNumber ? 'Step 2: Set Up Your Stripe Account' : 'Set Up Your Stripe Account'}
        </h3>
        <p className="text-sm text-indigo-800 mb-3">
          You'll be redirected to Stripe to set up your payment account and add your bank details.
          This is how you'll receive the money.
        </p>

        <div className="bg-white rounded-lg p-3 mb-3 border border-indigo-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">💡 What to expect on Stripe:</p>
          <p className="text-xs text-gray-600 mb-2">
            Stripe needs a few details to securely send your funds. Most people select{' '}
            <strong>"Individual / Sole Proprietor"</strong> — you don't need a business to receive
            gifts!
          </p>
          <p className="text-xs text-gray-600">
            You'll provide your name, address, and bank account details. The whole process takes
            about 2-3 minutes.
          </p>
        </div>

        <ul className="space-y-1 text-xs text-indigo-700">
          <li>✓ Secure bank account connection</li>
          <li>✓ Funds arrive in 1-2 business days</li>
          <li>✓ No fees deducted from your gift</li>
        </ul>
      </div>
    );
  }

  // Simple version for signup/accept flows
  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5"
        >
          <path
            fillRule="evenodd"
            d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
            clipRule="evenodd"
          />
        </svg>
        <div className="flex-1">
          <p className="text-xs font-semibold text-indigo-800 mb-0.5">Receive Gifts via PayPal</p>
          <p className="text-xs text-indigo-600 leading-relaxed">
            Add your PayPal email after signing up to receive gifts directly. Setup is quick.
          </p>
        </div>
      </div>
    </div>
  );
}

export default StripeSetupInfo;
